

import React, { memo, useState, useCallback, useEffect } from 'react';
import cx from 'classnames';
import _findKey from 'lodash.findkey';
import { VIDEO_OUTPUT_STATUS } from '@shootsta/common-domain-data';
import { Icon, Text } from '../../../ui-kit';

import OptionsMenu from './OptionsMenu';
import { LANGUAGES } from '../../Addons/constants/captions';
import { getNextSpeed } from '../utils';

import iconColor from '../constants/iconColor';
import iconHoverColor from '../constants/iconHoverColor';
import iconDisabledColor from '../constants/iconDisabledColor';
import maxVolumeValue from '../constants/maxVolumeValue';
import SKIP_METHODS from '../constants/skipMethods';
import SPEEDS from '../constants/speeds';

















































function getVolumeIcon(volume) {
  if (!volume) {
    return 'speakermute';
  }

  if (volume <= 0.5) {
    return 'speakermin';
  }

  if (volume <= 1) {
    return 'speakermax';
  }

  return 'speakermute';
}

function parseSeconds(seconds) {
  const minutes = `${Math.floor(seconds / 60)}`;
  const roundedSeconds = (seconds % 60).toFixed(2);
  const split = roundedSeconds.split('.');

  return `${minutes}:${split[0].padStart(2, '0')}`;
}

function getCaptionOption(caption, onSelectCaption) {
  try {
    const { lang } = typeof caption.metaData === 'string' ?
    JSON.parse(caption.metaData) :
    caption.metaData;

    return {
      label: LANGUAGES[lang.toUpperCase()],
      onClick: onSelectCaption(caption)
    };
  } catch (err) {
    return { label: 'Unknown', onClick: onSelectCaption(caption) };
  }
}

function hasEnglishCaption(captions) {
  if (!captions || !Array.isArray(captions) || !captions.length) {
    return false;
  }
  return captions.some((caption) => {
    try {
      const { lang } = typeof caption.metaData === 'string' ?
      JSON.parse(caption.metaData) :
      caption.metaData;
      return lang && lang.toUpperCase() === 'EN';
    } catch (err) {
      return false;
    }
  });
}

function getEditCaptionActionOption(captions, onEditCaptionClick, hasCaptions, isCaptionGenerating, hasGenerationError) {
  const hasEN = hasEnglishCaption(captions);
  const getLabel = () => {
    if (isCaptionGenerating) {return 'Generating Captions...';}
    if (hasGenerationError) {return 'Generation Failed!';}
    return hasEN ? 'Edit Caption (EN)' : 'Generate Caption (EN)';
  };
  return {
    label: getLabel(),
    onClick: () => onEditCaptionClick({
      type: hasEN ? 'edit' : 'generate'
    }),
    isAction: true,
    hasDivider: hasCaptions
  };
}

function Controls({
  currentTime,
  toggleVideoPlay,
  toggleFullscreen,
  toggleHD,
  toggleVolume,
  setVolume,
  toggleComments,
  isPlaying,
  volume,
  isCommentsEnabled,
  showComments,
  isHD,
  isFullscreen,
  hdAvailable,
  captions,
  selectedCaption,
  loading,
  onSelectCaption,
  videoStatus,
  totalDuration,
  hideCaptionButton,
  showFullscreenButton = true,
  onSkipForwardOrBackward,
  onChangeSpeed,
  loadDefaultCaptions = false,
  isCaptionsLoaded,
  showDownload = false,
  onClickDownload,
  onEditCaptionClick,
  isCaptionGenerating,
  hasGenerationError
}) {
  const subtitles = captions || [];
  const [showLabels, setIconLabelsVisibility] = useState(true);
  const [speed, setSpeed] = useState(SPEEDS.x10);

  useEffect(() => {
    if (
    loadDefaultCaptions &&
    !isCaptionsLoaded &&
    !hideCaptionButton &&
    captions &&
    Array.isArray(captions) &&
    captions.length &&
    selectedCaption === undefined &&
    onSelectCaption)
    {
      onSelectCaption(captions[0])();
    }
  }, [loadDefaultCaptions, captions, selectedCaption, isCaptionsLoaded]);

  function setVolumeHandler(event) {
    // $FlowFixMe
    setVolume(event.target.value / maxVolumeValue);
  }

  function getVolume() {
    return volume * maxVolumeValue;
  }

  const handleChangeSpeed = useCallback(() => {
    const nextSpeed = getNextSpeed(speed);
    setSpeed(nextSpeed);
    onChangeSpeed(nextSpeed);
  }, [speed]);

  const isSkipBackwardDisabled = loading || currentTime <= 0;
  const isSkipForwardDisabled = loading || currentTime >= totalDuration - 1;

  const renderSkipForwardOrBackwardIcon = (method) => {
    const isDisabled = method === SKIP_METHODS.forward ?
    isSkipForwardDisabled :
    isSkipBackwardDisabled;

    const iconName = method === SKIP_METHODS.forward ? 'forward102' : 'rewind102';

    return (
      <Icon
        className={cx(
          {
            [`player__control-bar__skip-${method}-icon`]: showLabels,
            'player__control-bar--disabled': isDisabled
          },
          'player__control-bar--desktop-only'
        )}
        onClick={
        isDisabled ?
        undefined :
        () => {
          onSkipForwardOrBackward(method);
        }}

        name={iconName}
        color={isDisabled ? iconDisabledColor : iconColor}
        hoverColor={isDisabled ? iconDisabledColor : iconHoverColor}
        size={20} />);


  };

  const renderSpeedIcon = () => {
    const iconName = `speed${_findKey(SPEEDS, (value) => value === speed)}`;

    return (
      <Icon
        className={cx(
          'player__control-bar__speed-icon',
          { [`player__control-bar__${iconName}-icon`]: showLabels },
          'player__control-bar--desktop-only'
        )}
        onClick={!loading && handleChangeSpeed}
        name={iconName}
        color={iconColor}
        hoverColor={iconHoverColor}
        size={25} />);


  };

  const renderTime = () =>
  <>
      <span>{parseSeconds(currentTime)}</span>
      <p>/</p>
      <span>{parseSeconds(totalDuration)}</span>
    </>;


  return (
    <div className="player__control-bar">
      <div
        className={cx('player__control-bar__controls', {
          'player__control-bar__controls--loading': loading
        })}>

        <div
          className={cx(
            'player__control-bar__controls__section',
            'player__control-bar__controls__section--start',
            {
              'player__control-bar__controls__section--65-percent-width':
              videoStatus !== VIDEO_OUTPUT_STATUS.APPROVED,
              'player__control-bar__controls__section--80-percent-width':
              videoStatus !== VIDEO_OUTPUT_STATUS.APPROVED && !showComments
            }
          )}>

          <Icon
            className={cx({ 'player__control-bar__play-icon': showLabels })}
            onClick={!loading && toggleVideoPlay}
            name={isPlaying ? 'pause' : 'play'}
            color={iconColor}
            hoverColor={iconHoverColor}
            size={20} />


          {renderSpeedIcon()}

          {renderSkipForwardOrBackwardIcon(SKIP_METHODS.backward)}

          {renderSkipForwardOrBackwardIcon(SKIP_METHODS.forward)}

          <Icon
            className="player__control-bar__volume-icon"
            onClick={!loading && toggleVolume}
            name={getVolumeIcon(volume)}
            color={iconColor}
            hoverColor={iconHoverColor}
            size={25}
            disabled={loading} />

          <input
            type="range"
            className="player__control-bar__controls__volume-bar"
            value={getVolume()}
            onInput={setVolumeHandler} />


          {videoStatus !== VIDEO_OUTPUT_STATUS.APPROVED &&
          <p className="player__control-bar__controls__time">
              {renderTime()}
            </p>}

        </div>

        {videoStatus === VIDEO_OUTPUT_STATUS.APPROVED &&
        <div className="player__control-bar__controls__section player__control-bar__controls__section--center player__control-bar__controls__section--time-container">
            {renderTime()}
          </div>}


        <div
          className={cx(
            'player__control-bar__controls__section',
            'player__control-bar__controls__section--end',
            {
              'player__control-bar__controls__section--35-percent-width':
              videoStatus !== VIDEO_OUTPUT_STATUS.APPROVED,
              'player__control-bar__controls__section--30-percent-width':
              videoStatus !== VIDEO_OUTPUT_STATUS.APPROVED && !showComments
            }
          )}>

          {showComments &&
          <div
            role="button"
            tabIndex={0}
            onClick={toggleComments}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                toggleComments();
              }
            }}>

              <Text
              className={cx('player__control-bar__pins', {
                'player__control-bar__pins--active': isCommentsEnabled
              })}
              heading5>

                {isCommentsEnabled ? 'Hide Pins' : 'Show Pins'}
              </Text>
            </div>}


          {!hideCaptionButton && (
          captions && Array.isArray(captions) && captions.length ||
          onEditCaptionClick) &&
          <OptionsMenu
            options={[
            ...subtitles.map(
              (caption) => getCaptionOption(caption, onSelectCaption)
            ),
            ...(onEditCaptionClick ?
            [getEditCaptionActionOption(
              subtitles, onEditCaptionClick, subtitles.length > 0, isCaptionGenerating, hasGenerationError
            )] :
            [])]}

            selectedIndex={subtitles.findIndex(
              (caption) => caption === selectedCaption
            )}
            setIconLabelsVisibility={setIconLabelsVisibility}
            disabled={loading}>

                <Icon
              className={cx({
                'player__control-bar__captions-icon': showLabels
              })}
              name="captions"
              color={selectedCaption ? 'white' : iconColor}
              hoverColor={
              subtitles.length || onEditCaptionClick ?
              iconHoverColor :
              void 0}

              size={25} />

              </OptionsMenu>}

          {showDownload &&
          <Icon
            className={cx({ 'player__control-bar__download-icon': showLabels })}
            onClick={!loading && onClickDownload}
            name="download"
            color="white"
            hoverColor="white75"
            size={20} />}


          {hdAvailable &&
          <Icon
            className={cx({ 'player__control-bar__def-icon': showLabels })}
            onClick={!loading && toggleHD}
            name="hd"
            color={isHD ? 'white' : iconColor}
            hoverColor={isHD ? 'white75' : iconHoverColor}
            size={25} />}


          {showFullscreenButton &&
          <Icon
            className={cx({
              'player__control-bar__fullscreen-icon': showLabels
            })}
            onClick={!loading && toggleFullscreen}
            name={isFullscreen ? 'defscreen' : 'fullscreen'}
            color={iconColor}
            hoverColor={iconHoverColor}
            size={20} />}


        </div>
      </div>
    </div>);

}

export default memo(Controls);